import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";

export const Gallery = () => {
  const { content } = useContent();
  const { t, L } = useLang();
  const images = content?.gallery || [];
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(images.map((i) => i.category).filter(Boolean)));
    return ["All", ...cats];
  }, [images]);

  const filtered = filter === "All" ? images : images.filter((i) => i.category === filter);

  return (
    <section id="gallery" className="bg-sand-100 py-20 sm:py-28" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow text-coral mb-4">{t("gallery.eyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-ocean leading-[1.05]">
              {t("gallery.title")}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`gallery-filter-${cat}`}
                onClick={() => setFilter(cat)}
                className={`font-mono text-xs tracking-[0.12em] uppercase px-4 py-2 rounded-full border transition-colors ${
                  filter === cat
                    ? "bg-ocean text-sand-100 border-ocean"
                    : "bg-transparent text-ocean/70 border-ocean/20 hover:border-ocean"
                }`}
              >
                {cat === "All" ? t("gallery.all") : cat}
              </button>
            ))}
          </div>
        </div>

        <div className="columns-2 md:columns-3 lg:columns-4 gap-3 sm:gap-4 [column-fill:_balance]">
          {filtered.map((img, i) => (
            <motion.button
              key={`${img.url}-${i}`}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
              onClick={() => setLightbox(img)}
              data-testid={`gallery-item-${i}`}
              className="mb-3 sm:mb-4 block w-full overflow-hidden rounded-xl group relative"
            >
              <img src={img.url} alt={L(img.alt)} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-ocean-900/0 group-hover:bg-ocean-900/20 transition-colors" />
            </motion.button>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-ocean-900/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button className="absolute top-6 right-6 text-sand-100" onClick={() => setLightbox(null)} data-testid="lightbox-close">
            <X className="w-8 h-8" />
          </button>
          <img src={lightbox.url} alt={L(lightbox.alt)} className="max-h-[85vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
};
